/**
 * Wire envelope for every frame: discriminated union on `t`.
 * Shared verbatim by server and client - there is no second definition.
 */
import type { ProtocolError } from './errors.ts';

// ── client → server ────────────────────────────────────────────────────────

export interface HelloFrame {
  t: 'hello';
  v: number;
  /** null before the owner has imported a credential. */
  kid: string | null;
  /** Per-browser install id, generated once at credential import. */
  install: string;
  /** topic -> last seen seq, so a reconnect resumes instead of re-snapshotting. */
  resume?: Record<string, number>;
}

export interface AuthFrame {
  t: 'auth';
  kid: string;
  /** base64url of the ECDSA signature over the canonical payload. */
  sig: string;
  /** Client nonce, folded into the signed payload. */
  cnonce: string;
}

export interface ReqFrame {
  t: 'req';
  id: number;
  m: string;
  p?: unknown;
}

export interface SubFrame {
  t: 'sub';
  id: number;
  topic: string;
  since?: number;
}

export interface UnsubFrame {
  t: 'unsub';
  id: number;
  topic: string;
}

export interface PongFrame {
  t: 'pong';
  ts: number;
}

/**
 * Регистрация агента. Единственный кадр, обрабатываемый ДО авторизации,
 * кроме hello и auth, — и иначе быть не может: агент приходит без ключа,
 * именно за ним он и пришёл.
 *
 * Отдельный кадр, а не метод: методы требуют открытой сессии, а открыть её
 * без зарегистрированного ключа нельзя. Заводить ради этого «метод без
 * авторизации» значит проделать в проверке дыру, через которую однажды
 * пролезет что-то ещё.
 */
export interface EnrollFrame {
  t: 'enroll';
  token: string;
  publicKeyJwk: { kty: 'EC'; crv: 'P-256'; x: string; y: string };
  /** Хост, а не сайт: сайты берутся из настроек и проверяются отдельно. */
  host: { name: string; note?: string };
  /** Постоянный идентификатор установки агента. */
  install: string;
}

export type ClientFrame =
  | HelloFrame | AuthFrame | EnrollFrame | ReqFrame | SubFrame | UnsubFrame | PongFrame;

// ── server → client ────────────────────────────────────────────────────────

export interface ChallengeFrame {
  t: 'challenge';
  sid: string;
  nonce: string;
  ts: number;
}

export interface AuthOkFrame {
  t: 'auth.ok';
  session: string;
  owner: string;
  kid: string;
  label: string;
  ssrToken: string;
  expDays: number;
}

export interface EnrollOkFrame {
  t: 'enroll.ok';
  kid: string;
  /** Присвоен консолью. Агент хранит его рядом с ключом навсегда. */
  hostId: string;
  issuer: string;
  fingerprint: string;
  /**
   * Читаемое имя машины: для журналов и для имени ведра.
   *
   * Отдельно от hostId намеренно. Тот непрозрачен и должен быть таким —
   * область дедупликации не может зависеть от имени. Но в журнале
   * «host-e6d2e6e3f5» не говорит человеку ничего, а «web-eurobrands-shop-de»
   * говорит всё.
   */
  /**
   * DNS-имя машины: то, что владелец ввёл при регистрации.
   *
   * Отдельно от псевдонима, потому что замена точек на тире необратима — по
   * `docker-helsinki-eu-brands-de` не восстановить, где стояли точки, а
   * `eu-brands-de` и `eu.brands.de` разные вещи. Агенту оно нужно, чтобы
   * сверить выданное ведро с правилом и сказать вслух, если они разошлись.
   */
  hostDns: string;
  hostAlias: string;
  /**
   * Куда писать. Выдаётся при РЕГИСТРАЦИИ, а не в настройках.
   *
   * Ведро — не предпочтение владельца, которое он завтра поменяет, а свойство
   * машины, определённое в тот момент, когда машину завели. Приди оно с
   * настройками — был бы промежуток, в котором агент зарегистрирован, но не
   * знает, куда пишет.
   */
  storage: { endpoint: string; bucket: string; region: string };
}

export interface EnrollErrFrame {
  t: 'enroll.err';
  code: 'unknown_token' | 'used' | 'expired' | 'bad_key' | 'bad_frame';
  msg: string;
}

export type AuthErrorCode =
  | 'unknown_kid' | 'revoked' | 'bad_sig' | 'expired_challenge'
  | 'clock_skew' | 'not_ready';

export interface AuthErrFrame {
  t: 'auth.err';
  code: AuthErrorCode;
  msg: string;
}

export type ResFrame =
  | { t: 'res'; id: number; ok: true;  d: unknown }
  | { t: 'res'; id: number; ok: false; e: ProtocolError };

export interface EventFrame {
  t: 'event';
  topic: string;
  seq: number;
  d: unknown;
}

export interface PingFrame {
  t: 'ping';
  ts: number;
}

/**
 * Ответ на `ping` клиента.
 *
 * Проверка живости двусторонняя: спрашивать может любая сторона, отвечает
 * другая. Раньше сервер умел только спрашивать, и агент, прочитавший таблицу
 * кадров буквально, получал разрыв на своём первом `ping`.
 */
export interface PongFrame {
  t: 'pong';
  ts: number;
}

export interface ByeFrame {
  t: 'bye';
  code: 'revoked' | 'shutting_down' | 'slow_consumer' | 'protocol_error';
  msg?: string;
}

/** Development only: the vite watcher saw a change, reload the page.
 *  Sent over the application socket so no second WebSocket is needed and
 *  nothing extra has to be proxied through nginx. */
export interface DevReloadFrame {
  t: 'dev.reload';
  reason: string;
}

export type ServerFrame =
  | ChallengeFrame | AuthOkFrame | AuthErrFrame
  | EnrollOkFrame | EnrollErrFrame | ResFrame
  | EventFrame | PingFrame | PongFrame | ByeFrame | DevReloadFrame;

/**
 * The exact string signed by the owner key. Both sides build it with this
 * function - a mismatch here is an authentication bug that looks like a
 * "wrong key", so it lives in exactly one place.
 */
export function signaturePayload(input: {
  prefix: string;
  issuer: string;
  kid: string;
  nonce: string;
  ts: number;
  cnonce: string;
}): string {
  return [
    input.prefix, input.issuer, input.kid,
    input.nonce, String(input.ts), input.cnonce,
  ].join('|');
}
